// Event URLs: /events/<slug>/ built from the event title.
//
// Slugs are plain ASCII so a copied link reads as text
// (/events/nool-oli-arangkam-western-sydney-tamil-study-centre/) instead of
// %E0%AE%.. codes. Tamil letters are romanised with a common, readable scheme;
// the page itself still shows the Tamil title.
//
// Links shared before this change used Tamil-script slugs (legacySlugify).
// They still resolve: EventDetails and public/event-page.php recognise them
// and redirect to the ASCII URL.
//
// Keep in step with public/event-slug.php (same tables and rules); the
// `npm run seo:check` script compares both on the live event titles.

const VOWELS = {
  அ: "a", ஆ: "aa", இ: "i", ஈ: "ee", உ: "u", ஊ: "oo",
  எ: "e", ஏ: "e", ஐ: "ai", ஒ: "o", ஓ: "o", ஔ: "au", ஃ: "h",
};

const CONSONANTS = {
  க: "k", ங: "ng", ச: "s", ஞ: "nj", ட: "d", ண: "n", த: "th", ந: "n",
  ப: "p", ம: "m", ய: "y", ர: "r", ல: "l", வ: "v", ழ: "zh", ள: "l",
  ற: "r", ன: "n", ஜ: "j", ஷ: "sh", ஸ: "s", ஹ: "h",
};

// Vowel signs that follow a consonant; the pulli (்) removes the vowel.
const SIGNS = {
  "ா": "aa", "ி": "i", "ீ": "ee", "ு": "u", "ூ": "oo", "ெ": "e",
  "ே": "e", "ை": "ai", "ொ": "o", "ோ": "o", "ௌ": "au", "்": "", "ௗ": "",
};

const TAMIL_DIGITS = "௦௧௨௩௪௫௬௭௮௯";

// Tamil script -> Latin letters; other characters pass through unchanged.
export const romaniseTamil = (text = "") => {
  const chars = [...text.normalize("NFC")];
  let out = "";
  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    if (CONSONANTS[ch] !== undefined) {
      const next = chars[i + 1];
      if (SIGNS[next] !== undefined) {
        out += CONSONANTS[ch] + SIGNS[next];
        i++;
      } else {
        out += `${CONSONANTS[ch]}a`;
      }
    } else if (VOWELS[ch] !== undefined) {
      out += VOWELS[ch];
    } else if (SIGNS[ch] !== undefined) {
      out += SIGNS[ch];
    } else if (TAMIL_DIGITS.includes(ch)) {
      out += String(TAMIL_DIGITS.indexOf(ch));
    } else {
      out += ch;
    }
  }
  return out;
};

// ASCII slug: romanise Tamil, drop accents, keep a-z/0-9 runs joined by "-".
export const slugify = (text = "") =>
  romaniseTamil(text.toString().trim())
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// The Tamil-script slug used before 26 Sep 2026 (grapheme-aware so vowel
// signs stay attached). Only used to recognise and redirect old links.
export const legacySlugify = (text = "") => {
  const str = text.toString().trim().toLowerCase();
  if (typeof Intl !== "undefined" && Intl.Segmenter) {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    const words = [];
    let current = "";
    for (const { segment } of segmenter.segment(str)) {
      if (/[\p{L}\p{N}]/u.test(segment)) current += segment;
      else if (current) {
        words.push(current);
        current = "";
      }
    }
    if (current) words.push(current);
    return words.join("-");
  }
  return str.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-+|-+$/g, "");
};

// Slug for an event; falls back to its id if the title has no usable letters.
export const eventSlug = (event) =>
  slugify(event?.article_title) || (event?.aid ? `event-${event.aid}` : "event");

// Build the "/events/title-slug" path for an event article (no id in the URL)
export const buildEventPath = (event) => `/events/${eventSlug(event)}`;

// The API has no slug lookup, so find the matching article out of a fetched
// list. Returns { event, legacy } - legacy is true when the URL used the old
// Tamil-script slug, so the caller can redirect to the ASCII URL.
export const findEventBySlug = (events = [], slug = "") => {
  let wanted = slug;
  try {
    wanted = decodeURIComponent(slug);
  } catch {
    // already decoded (React Router does this) or not valid %-encoding
  }
  wanted = wanted.normalize("NFC");
  const current = events.find((event) => eventSlug(event) === wanted);
  if (current) return { event: current, legacy: false };
  const old = events.find((event) => legacySlugify(event.article_title) === wanted);
  return old ? { event: old, legacy: true } : { event: null, legacy: false };
};
